import type { Resource } from '../hooks/api/useFetchResources';

/**
 * Resource Cache Utility
 *
 * Implements stale-while-revalidate caching for resource data.
 * - Caches resources by organization and type in localStorage
 * - Expires cache entries after 5 minutes
 * - Returns cached data instantly while fetching fresh data in background
 * - Handles cache invalidation when org changes
 */

const CACHE_PREFIX = 'genesys_resource_cache';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const CACHE_VERSION = '1'; // Increment to invalidate all caches on structure change

interface CacheEntry {
  data: Resource[];
  timestamp: number;
  orgName: string;
  resourceType: string;
  version: string;
}

/**
 * Generate cache key for a specific org + resource type combination
 */
const getCacheKey = (orgName: string, resourceType: string): string => {
  return `${CACHE_PREFIX}_${orgName}_${resourceType}`;
};

/**
 * Check if a cache entry is still valid (not expired)
 */
const isCacheValid = (entry: CacheEntry): boolean => {
  const now = Date.now();
  const age = now - entry.timestamp;
  return age < CACHE_DURATION_MS && entry.version === CACHE_VERSION;
};

/**
 * Get cached resources for a specific org + type
 * Returns null if not cached or expired
 */
export const getCachedResources = (
  orgName: string,
  resourceType: string
): Resource[] | null => {
  try {
    const key = getCacheKey(orgName, resourceType);
    const cached = localStorage.getItem(key);

    if (!cached) {
      return null;
    }

    const entry: CacheEntry = JSON.parse(cached);

    // Validate cache entry structure
    if (!entry.data || !entry.timestamp || !entry.orgName || !entry.resourceType) {
      localStorage.removeItem(key);
      return null;
    }

    // Check if expired
    if (!isCacheValid(entry)) {
      localStorage.removeItem(key);
      return null;
    }

    // Verify org and type match (extra safety)
    if (entry.orgName !== orgName || entry.resourceType !== resourceType) {
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch (error) {
    // If localStorage is full or unavailable, fail silently
    console.warn('Failed to read from cache:', error);
    return null;
  }
};

/**
 * Store resources in cache for a specific org + type
 */
export const setCachedResources = (
  orgName: string,
  resourceType: string,
  data: Resource[]
): void => {
  try {
    const key = getCacheKey(orgName, resourceType);
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      orgName,
      resourceType,
      version: CACHE_VERSION
    };

    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    // If localStorage is full, just log warning - cache is optional
    console.warn('Failed to cache resources:', error);
  }
};

/**
 * Clear cache for a specific org + type
 */
export const clearCachedResource = (orgName: string, resourceType: string): void => {
  try {
    const key = getCacheKey(orgName, resourceType);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear cache:', error);
  }
};

/**
 * Clear all cached resources for a specific organization
 * Useful when switching orgs or on logout
 */
export const clearOrgCache = (orgName: string): void => {
  try {
    const keysToRemove: string[] = [];

    // Find all keys for this org
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${CACHE_PREFIX}_${orgName}_`)) {
        keysToRemove.push(key);
      }
    }

    // Remove them
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear org cache:', error);
  }
};

/**
 * Clear all resource caches (useful for logout or version change)
 */
export const clearAllResourceCaches = (): void => {
  try {
    const keysToRemove: string[] = [];

    // Find all cache keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    // Remove them
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear all caches:', error);
  }
};
